import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Contact, Campaign, Flow, Segment, DashboardMetrics } from '../types';

interface AppState {
  darkMode: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    company?: string;
  } | null;
  contacts: Contact[];
  campaigns: Campaign[];
  flows: Flow[];
  segments: Segment[];
  dashboardMetrics: DashboardMetrics;
}

type AppAction =
  | { type: 'TOGGLE_DARK_MODE' }
  | { type: 'UPDATE_USER'; payload: Partial<AppState['user']> }
  | { type: 'SET_CONTACTS'; payload: Contact[] }
  | { type: 'ADD_CONTACT'; payload: Contact }
  | { type: 'UPDATE_CONTACT'; payload: Contact }
  | { type: 'DELETE_CONTACT'; payload: string }
  | { type: 'SET_CAMPAIGNS'; payload: Campaign[] }
  | { type: 'ADD_CAMPAIGN'; payload: Campaign }
  | { type: 'UPDATE_CAMPAIGN'; payload: Campaign }
  | { type: 'SET_FLOWS'; payload: Flow[] }
  | { type: 'SET_SEGMENTS'; payload: Segment[] }
  | { type: 'SET_DASHBOARD_METRICS'; payload: DashboardMetrics };

const initialState: AppState = {
  darkMode: localStorage.getItem('darkMode') === 'true',
  user: null,
  contacts: [],
  campaigns: [],
  flows: [],
  segments: [],
  dashboardMetrics: {
    totalContacts: 0,
    activeContacts: 0,
    campaignsSent: 0,
    totalRevenue: 0,
    deliveryRate: 0,
    clickRate: 0,
    growthRate: 0,
    revenueGrowth: 0
  }
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'TOGGLE_DARK_MODE':
      const newDarkMode = !state.darkMode;
      localStorage.setItem('darkMode', newDarkMode.toString());
      return { ...state, darkMode: newDarkMode };
    case 'UPDATE_USER':
      return { ...state, user: { ...(state.user ?? {}), ...action.payload } as AppState['user'] };
    case 'SET_CONTACTS':
      return { ...state, contacts: action.payload };
    case 'ADD_CONTACT':
      return { ...state, contacts: [...state.contacts, action.payload] };
    case 'UPDATE_CONTACT':
      return {
        ...state,
        contacts: state.contacts.map(contact =>
          contact.id === action.payload.id ? action.payload : contact
        )
      };
    case 'DELETE_CONTACT':
      return {
        ...state,
        contacts: state.contacts.filter(contact => contact.id !== action.payload)
      };
    case 'SET_CAMPAIGNS':
      return { ...state, campaigns: action.payload };
    case 'ADD_CAMPAIGN':
      return { ...state, campaigns: [...state.campaigns, action.payload] };
    case 'UPDATE_CAMPAIGN':
      return {
        ...state,
        campaigns: state.campaigns.map(campaign =>
          campaign.id === action.payload.id ? action.payload : campaign
        )
      };
    case 'SET_FLOWS':
      return { ...state, flows: action.payload };
    case 'SET_SEGMENTS':
      return { ...state, segments: action.payload };
    case 'SET_DASHBOARD_METRICS':
      return { ...state, dashboardMetrics: action.payload };
    default:
      return state;
  }
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};